<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import camelcaseKeys from 'camelcase-keys';
import { useAlert } from 'dashboard/composables';
import { useKontaCompaniesStore } from 'dashboard/stores/kontaCompanies';
import KontaCompanyAPI from 'dashboard/api/kontaCompanies';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const emit = defineEmits(['edit', 'deleted']);

const { t } = useI18n();
const kontaCompaniesStore = useKontaCompaniesStore();

const dialogRef = ref(null);
const deleteDialogRef = ref(null);
const company = ref(null);
const isLoading = ref(false);

const displayName = computed(
  () => company.value?.name || t('KONTA_COMPANIES.UNNAMED')
);
const contacts = computed(() => company.value?.contacts || []);
const isDeleting = computed(() => kontaCompaniesStore.getUIFlags.deletingItem);

const fetchCompany = async id => {
  isLoading.value = true;
  try {
    const { data } = await KontaCompanyAPI.show(id);
    company.value = camelcaseKeys(data, { deep: true });
  } catch (error) {
    useAlert(t('KONTA_COMPANIES.DETAILS.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const open = async id => {
  company.value = null;
  dialogRef.value?.open();
  await fetchCompany(id);
};

const onEdit = () => {
  dialogRef.value?.close();
  emit('edit', company.value);
};

const onClickDelete = () => deleteDialogRef.value?.open();

const handleDelete = async () => {
  try {
    await kontaCompaniesStore.delete(company.value.id);
    useAlert(t('KONTA_COMPANIES.DELETE.SUCCESS'));
    deleteDialogRef.value?.close();
    dialogRef.value?.close();
    emit('deleted');
  } catch (error) {
    useAlert(t('KONTA_COMPANIES.DELETE.ERROR'));
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('KONTA_COMPANIES.DETAILS.TITLE')"
    :show-confirm-button="false"
    :show-cancel-button="false"
    width="lg"
  >
    <div v-if="isLoading" class="flex items-center justify-center py-8">
      <span class="text-sm text-n-slate-11">
        {{ t('KONTA_COMPANIES.LOADING') }}
      </span>
    </div>
    <div v-else-if="company" class="flex flex-col gap-5">
      <div class="flex items-center gap-4">
        <Avatar
          :src="company.avatarUrl || null"
          :name="company.name || displayName"
          :size="56"
          hide-offline-status
          rounded-full
        />
        <div class="flex flex-col gap-1 min-w-0">
          <span class="text-base font-medium truncate text-n-slate-12">
            {{ displayName }}
          </span>
          <a
            v-if="company.domain"
            :href="`https://${company.domain}`"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1.5 text-sm text-n-blue-11 truncate hover:underline"
          >
            <Icon icon="i-lucide-globe" size="size-3.5 text-n-blue-11" />
            <span class="truncate">{{ company.domain }}</span>
          </a>
        </div>
      </div>

      <p v-if="company.description" class="mb-0 text-sm text-n-slate-11">
        {{ company.description }}
      </p>

      <div class="flex items-center gap-4 text-sm text-n-slate-11">
        <span class="inline-flex items-center gap-1.5">
          <Icon icon="i-lucide-contact" size="size-3.5 text-n-slate-11" />
          {{
            t('KONTA_COMPANIES.CONTACTS_COUNT', {
              n: company.contactsCount || 0,
            })
          }}
        </span>
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('KONTA_COMPANIES.DETAILS.CONTACTS') }}
        </span>
        <div v-if="contacts.length === 0" class="text-sm text-n-slate-11">
          {{ t('KONTA_COMPANIES.DETAILS.NO_CONTACTS') }}
        </div>
        <ul v-else class="flex flex-col gap-2">
          <li
            v-for="contact in contacts"
            :key="contact.id"
            class="flex items-center gap-3 min-w-0"
          >
            <Avatar
              :src="contact.thumbnail || null"
              :name="contact.name || ''"
              :size="32"
              hide-offline-status
              rounded-full
            />
            <div class="flex flex-col min-w-0">
              <span class="text-sm truncate text-n-slate-12">
                {{ contact.name }}
              </span>
              <span
                v-if="contact.email"
                class="text-xs truncate text-n-slate-11"
              >
                {{ contact.email }}
              </span>
            </div>
          </li>
        </ul>
      </div>

      <div class="flex items-center justify-end w-full gap-3">
        <Button
          variant="faded"
          color="ruby"
          :label="t('KONTA_COMPANIES.DETAILS.DELETE')"
          type="button"
          @click="onClickDelete"
        />
        <Button
          color="blue"
          :label="t('KONTA_COMPANIES.DETAILS.EDIT')"
          type="button"
          @click="onEdit"
        />
      </div>
    </div>
  </Dialog>

  <Dialog
    ref="deleteDialogRef"
    type="alert"
    :title="t('KONTA_COMPANIES.DELETE.CONFIRM_TITLE')"
    :description="t('KONTA_COMPANIES.DELETE.CONFIRM_MESSAGE')"
    :confirm-button-label="t('KONTA_COMPANIES.DETAILS.DELETE')"
    :is-loading="isDeleting"
    @confirm="handleDelete"
  />
</template>
